import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { getUserFacingErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import type { AsyncStatus } from './useAsyncResource';

export interface PagedPage<T> {
  items: readonly T[];
  hasNextPage: boolean;
}

export interface PagedList<T> {
  /** Every page fetched so far, in order. */
  items: readonly T[];
  status: AsyncStatus;
  /** A first page is loading behind items that are already listed. */
  refreshing: boolean;
  /** The next page is loading; the items on screen stay put. */
  loadingMore: boolean;
  hasNextPage: boolean;
  error: string | null;
  loadMore: () => void;
  reload: () => void;
}

/**
 * A list the operator extends rather than steps through.
 *
 * Numbered pages suit a grid, where a row is one line and twenty of them fit on
 * a screen. A feed of posts is the opposite: a page of cards is far taller than
 * the column, so paging turns into "scroll to the bottom, click 2, scroll back
 * to the top" and the operator loses their place on every hop. Appending keeps
 * one continuous column, which is also how the member sees the same posts.
 *
 * `requestKey` identifies the query (tab, search, queue). Changing it starts the
 * list again at page one, and — as in `useAsyncResource` — the posts already on
 * screen stay while that page loads, so a keystroke never collapses the column
 * to a spinner.
 */
export function usePagedList<T>(
  loadPage: (page: number) => Promise<PagedPage<T>>,
  requestKey: string,
  fallbackErrorMessage: string,
): PagedList<T> {
  const [items, setItems] = useState<readonly T[]>([]);
  const [status, setStatus] = useState<AsyncStatus>('loading');
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const loadRef = useRef(loadPage);
  const fallbackRef = useRef(fallbackErrorMessage);
  const requestIdRef = useRef(0);
  const pageRef = useRef(1);
  // The appended result is built from a ref rather than from state: two clicks
  // in the same tick must not both append to the same snapshot.
  const itemsRef = useRef<readonly T[]>(items);
  const hasItemsRef = useRef(false);
  const busyRef = useRef(false);

  useLayoutEffect(() => {
    loadRef.current = loadPage;
    fallbackRef.current = fallbackErrorMessage;
  });

  const run = useCallback((page: number, mode: 'replace' | 'append') => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    busyRef.current = true;

    if (mode === 'append') {
      setLoadingMore(true);
    } else if (hasItemsRef.current) {
      setRefreshing(true);
    } else {
      setStatus('loading');
    }
    setError(null);

    Promise.resolve()
      .then(() => loadRef.current(page))
      .then(result => {
        if (requestIdRef.current !== requestId) {
          return;
        }
        const next =
          mode === 'append'
            ? [...itemsRef.current, ...result.items]
            : [...result.items];
        itemsRef.current = next;
        pageRef.current = page;
        hasItemsRef.current = true;
        busyRef.current = false;
        setItems(next);
        setHasNextPage(result.hasNextPage);
        setStatus('ready');
        setRefreshing(false);
        setLoadingMore(false);
      })
      .catch((cause: unknown) => {
        if (requestIdRef.current !== requestId) {
          return;
        }
        busyRef.current = false;
        setRefreshing(false);
        setLoadingMore(false);
        setError(getUserFacingErrorMessage(cause, fallbackRef.current));
        if (mode === 'replace') {
          // Nothing trustworthy to show: the caller renders its error state, and
          // clearing the flag means a retry shows the placeholder rather than
          // leaving stale posts under an error message.
          hasItemsRef.current = false;
          setStatus('error');
        }
        // A failed `loadMore` keeps the pages already listed; the caller offers
        // the retry next to the button.
      });
  }, []);

  useEffect(() => {
    run(1, 'replace');
  }, [requestKey, reloadToken, run]);

  const loadMore = useCallback(() => {
    if (busyRef.current || !hasNextPage) {
      return;
    }
    run(pageRef.current + 1, 'append');
  }, [hasNextPage, run]);

  const reload = useCallback(() => {
    setReloadToken(token => token + 1);
  }, []);

  return {
    items,
    status,
    refreshing,
    loadingMore,
    hasNextPage,
    error,
    loadMore,
    reload,
  };
}
